import { fetchAuthSession } from "aws-amplify/auth";
import { getError } from "utils/errors";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

export const API = {
  async get(apiName, path, options = {}) {
    return request("GET", path, options);
  },

  async post(apiName, path, options = {}) {
    return request("POST", path, options);
  },

  async put(apiName, path, options = {}) {
    return request("PUT", path, options);
  },

  async patch(apiName, path, options = {}) {
    return request("PATCH", path, options);
  },

  async del(apiName, path, options = {}) {
    return request("DELETE", path, options);
  },

  async head(apiName, path, options = {}) {
    return request("HEAD", path, options, { parseJson: false });
  },
};

async function request(
  method,
  path,
  options = {},
  { parseJson = true } = {},
  attempt = 0
) {
  try {
    const requestOptions = await buildOptions(options);
    const url = buildUrl(path, requestOptions.queryParams);

    const res = await fetch(url, {
      method,
      headers: requestOptions.headers,
      body:
        requestOptions.body !== undefined
          ? JSON.stringify(requestOptions.body)
          : undefined,
    });

    if (res.status === 401 || res.status === 403) {
      if (attempt === 0) {
        // 🔄 force refresh & retry once
        await refreshSession();
        return request(method, path, options, { parseJson }, attempt + 1);
      }
    }

    if (!res.ok) {
      const text = await res.text();
      throw { response: { body: text }, status: res.status };
    }

    if (!parseJson) return res;
    return await res.json();
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

async function refreshSession() {
  await fetchAuthSession({ forceRefresh: true });
}

async function buildOptions(options = {}) {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();

  return {
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    body: options.body,
    queryParams: options.queryStringParameters,
  };
}

function buildUrl(path, queryParams) {
  const url = new URL(path, API_BASE_URL);

  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
}

function handleApiError(error) {
  try {
    if (
      error instanceof TypeError &&
      !error.response
    ) {
      getError({
        message: navigator.onLine === false
          ? "You appear to be offline. Please check your internet connection."
          : "Network error. Unable to reach the server."
      });
      return;
    }

    const body = error?.response?.body;

    if (body) {
      let errData;

      if (typeof body === "string") {
        try {
          errData = JSON.parse(body); // ✅ works for Amplify v6+
        } catch {
          errData = { message: body };
        }
      } else if (typeof body === "object") {
        errData = body;
      } else {
        errData = { message: "Unknown error" };
      }

      getError(errData);
    } else if (error?.$metadata?.httpStatusCode) {
      getError({
        message: `Request failed (${error.$metadata.httpStatusCode})`,
      });
    } else if (error?.status) {
      getError({
        message: `Request failed (${error.status})`,
      });
    } else {
      getError({ message: error.message || "Something went wrong" });
    }
  } catch (err) {
    console.error("Error in handleApiError:", err);
    getError({ message: "Something went wrong" });
  }
}
